import { useMediaQuery } from '@zg-rentals/react-hooks';

// Screen size breakpoints matching Constellation design tokens
const BREAKPOINTS = {
  sm: 320,
  md: 480,
  lg: 768,
  xl: 1024,
  xxl: 1280,
} as const;

export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ScreenType = 'mobile' | 'tablet' | 'desktop';

export const useScreenSize = () => {
  // initializeWithValue preserves this hook's original behavior of reading the real matchMedia
  // value on the client's first render, since this app renders via Pages Router hydration and
  // the many mobile/desktop layout branches keyed off these booleans shouldn't flash to "mobile"
  // while hydration settles.
  // Use pixel-based media queries matching Constellation breakpoints
  const isScreenXS = useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`, true); // < 320px
  const isScreenSM = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`, true); // 320-479px
  const isScreenMD = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`, true); // 480-767px
  const isScreenLG = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`, true); // 768-1023px
  const isScreenXL = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px) and (max-width: ${BREAKPOINTS.xxl - 1}px)`, true); // 1024-1279px
  const isScreenXXL = useMediaQuery(`(min-width: ${BREAKPOINTS.xxl}px)`, true); // 1280px+

  // Screen type groupings (matches legacy browserUtils)
  const isScreenMobile = isScreenXS || isScreenSM; // 0-479px
  const isScreenTablet = isScreenMD || isScreenLG; // 480-1023px
  const isScreenDesktop = isScreenXL || isScreenXXL; // 1024px+

  // Greater-than-or-equal-to helpers (matches legacy AppSelectors)
  const isScreenLgGTE = isScreenLG || isScreenXL || isScreenXXL; // >= 768px
  const isScreenXlGTE = isScreenXL || isScreenXXL; // >= 1024px
  const isScreenXxlGTE = isScreenXXL; // >= 1280px

  return {
    isScreenXS,
    isScreenSM,
    isScreenMD,
    isScreenLG,
    isScreenXL,
    isScreenXXL,
    isScreenMobile,
    isScreenTablet,
    isScreenDesktop,
    isScreenLgGTE,
    isScreenXlGTE,
    isScreenXxlGTE,
  };
};
